import { useEffect, useState } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWhatsAppOrder = () => {
    const message = encodeURIComponent('Hello EatSpot! I would like to place an order. Please share your menu details.');
    window.open(`https://wa.me/923140607452?text=${message}`, '_blank');
  };

  const handleVisit = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=vibrant%20colorful%20Pakistani%20street%20food%20spread%20featuring%20golden%20bun%20kebabs%20with%20green%20chutney%20dripping%2C%20crispy%20samosas%2C%20fresh%20rolls%2C%20colorful%20drinks%20and%20lassi%20in%20traditional%20glasses%2C%20arranged%20on%20rustic%20wooden%20table%20with%20bright%20orange%20and%20yellow%20tones%2C%20appetizing%20steam%20rising%2C%20professional%20food%20photography%20with%20shallow%20depth%20of%20field%20and%20bokeh%20background&width=1920&height=1080&seq=hero1&orientation=landscape')`
        }}
      />
      
      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>

      {/* Floating Decorative Elements */}
      <div className="absolute top-20 left-10 w-16 h-16 opacity-20 animate-bounce">
        <i className="ri-restaurant-2-fill text-6xl text-orange-400"></i>
      </div>
      <div className="absolute bottom-32 right-20 w-12 h-12 opacity-20 animate-pulse">
        <i className="ri-cake-3-fill text-5xl text-pink-400"></i>
      </div>
      <div className="absolute top-40 right-32 w-14 h-14 opacity-20 animate-bounce" style={{ animationDelay: '1s' }}>
        <i className="ri-goblet-fill text-5xl text-blue-400"></i>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <i className={`ri-restaurant-fill text-3xl ${scrolled ? 'text-orange-500' : 'text-white'}`}></i>
              <h1 className={`text-2xl font-bold ${scrolled ? 'text-gray-900' : 'text-white'}`}>EatSpot</h1>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#menu" className={`font-medium hover:text-orange-500 transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}>Menu</a>
              <a href="#about" className={`font-medium hover:text-orange-500 transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}>About</a>
              <a href="#gallery" className={`font-medium hover:text-orange-500 transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}>Gallery</a>
              <a href="#contact" className={`font-medium hover:text-orange-500 transition-colors whitespace-nowrap ${scrolled ? 'text-gray-700' : 'text-white'}`}>Contact</a>
              <button 
                onClick={handleWhatsAppOrder}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-full font-medium transition-all hover:scale-105 whitespace-nowrap"
              >
                <i className="ri-whatsapp-fill mr-2"></i>Order Now
              </button>
            </div>
            <button className="md:hidden">
              <i className={`ri-menu-line text-2xl ${scrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 text-center px-6 w-full max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-7xl md:text-9xl font-black text-white mb-4 tracking-tight" style={{ textShadow: '4px 4px 8px rgba(255, 107, 53, 0.5)' }}>
            EatSpot
          </h1>
          <p className="text-2xl md:text-3xl text-white font-light tracking-wide mb-2">
            Delicious Pakistani Fast Food, Fresh Every Day!
          </p>
          <p className="text-lg text-white/90 font-light" style={{ fontFamily: 'serif' }}>
            تازہ اور لذیذ پاکستانی فاسٹ فوڈ
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <button 
            onClick={handleWhatsAppOrder}
            className="group bg-orange-500 hover:bg-orange-600 text-white px-10 py-5 rounded-full text-lg font-semibold transition-all hover:scale-105 shadow-2xl flex items-center space-x-3 whitespace-nowrap"
          >
            <span className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
              <i className="ri-whatsapp-fill text-2xl text-green-500"></i>
            </span>
            <span>Order on WhatsApp</span>
          </button>
          <button 
            onClick={handleVisit}
            className="group bg-transparent hover:bg-white/10 text-white px-10 py-5 rounded-full text-lg font-semibold border-3 border-white transition-all hover:scale-105 flex items-center space-x-3 whitespace-nowrap"
          >
            <i className="ri-map-pin-fill text-2xl"></i>
            <span>Visit Us Today</span>
          </button>
        </div>
      </div>

      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#FFF8E1"/>
        </svg>
      </div>
    </div>
  );
}
